'use client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { wsManager } from '@/lib/wsManager';
import { useGlobalStore } from '@/store/useGlobalStore';
import type { Notification } from '@/store/useGlobalStore';

export interface LiveTicker {
  symbol: string;
  bid: number;
  ask: number;
  change: number;
  changePercent: number;
  timestamp: number;
}

export interface WidgetStats {
  today_profit: number;
  percent_change: number;
  roi: number;
  active_positions: number;
}

/** account_info.widget — financial snapshot of the currently active account */
export interface AccountWidget {
  balance: number;
  equity: number;
  free_margin: number;
  margin: number;
  pl: number;
  margin_level: number;
  credit: number;
}

export interface Position {
  id: number | string;
  symbol: string;
  amount: number;
  open_rate: number;
  current_rate: number;
  open_time: string;
  profit: number;
  profit_percent: number;
  day_change: number;
  day_change_percent: number;
}

interface ClientDataPayload {
  accounts?: Array<{
    account_id: string;
    type: string;
    balance: number;
  }>;
  account_info?: {
    widget?: Partial<AccountWidget>;
    positions?: {
      open?: Position[];
      closed?: Position[];
    };
  };
  widgets?: {
    dailyProfit?: { today_profit?: number; percent_change?: number };
    roi?: { today?: number };
    active_positions?: number;
  };
  notifications?: Array<{
    id?: string;
    uuid?: string;
    type?: 'info' | 'success' | 'warning' | 'error' | 'announcement' | 'support_request' | '';
    title?: string;
    message?: string;
    read?: boolean;
    timestamp?: string;
    created_at?: string;
    createdAt?: string;
  }>;
}

interface MarketTickPayload {
  symbol?: string;
  bid?: number;
  ask?: number;
  change?: number;
  change_percent?: number;
  changePercent?: number;
  timestamp?: number;
}

interface BalanceUpdatePayload {
  account_id?: string;
  balance?: number;
  equity?: number;
  margin?: number;
  free_margin?: number;
  margin_level?: number;
}

interface PositionsUpdatePayload {
  open?: Position[];
  closed?: Position[];
}

const EMPTY_POSITIONS: { open: Position[]; closed: Position[] } = { open: [], closed: [] };

/**
 * useDashboardStreams
 *
 * Subscribes to the WebSocket 'clientData' event (per WS_CONNECT.md):
 * - `accounts[]` → account list (id/type/balance), fed into the account switcher.
 * - `account_info.widget` → live financial snapshot of the currently active account.
 * - `account_info.positions` → open/closed positions for the active account.
 * - `widgets` → dashboard KPI cards (daily profit, ROI, active positions).
 * - `notifications[]` → real-time notification feed.
 */
export function useDashboardStreams() {
  const { activeAccount, updateAccount, addNotification, setNotifications } = useGlobalStore();
  const [tickers, setTickers] = useState<Record<string, LiveTicker>>({});
  const [widgetStats, setWidgetStats] = useState<WidgetStats>({
    today_profit: 0,
    percent_change: 0,
    roi: 0,
    active_positions: 0,
  });
  const [accountWidget, setAccountWidget] = useState<AccountWidget | null>(null);
  const [positions, setPositions] = useState(EMPTY_POSITIONS);
  const seenNotifIds = useRef<Set<string>>(new Set());

  // ── clientData handler (main WS event per WS_CONNECT.md) ─────────────────
  const handleClientData = useCallback(
    (raw: unknown) => {
      const data = raw as ClientDataPayload;
      if (!data) return;

      // Update account balances (accounts[] only carries account_id/type/balance)
      if (Array.isArray(data.accounts)) {
        data.accounts.forEach((acc) => {
          if (acc.account_id && acc.balance !== undefined) {
            updateAccount(acc.account_id, { balance: acc.balance });
          }
        });
      }

      // Update the active account's live financial widget (balance/equity/margin/...)
      const widget = data.account_info?.widget;
      if (widget) {
        setAccountWidget({
          balance: widget.balance ?? 0,
          equity: widget.equity ?? 0,
          free_margin: widget.free_margin ?? 0,
          margin: widget.margin ?? 0,
          pl: widget.pl ?? 0,
          margin_level: widget.margin_level ?? 0,
          credit: widget.credit ?? 0,
        });
        if (activeAccount?.account_id) {
          updateAccount(activeAccount.account_id, {
            ...(widget.balance !== undefined && { balance: widget.balance }),
            ...(widget.equity !== undefined && { equity: widget.equity }),
            ...(widget.margin !== undefined && { margin: widget.margin }),
            ...(widget.free_margin !== undefined && { freeMargin: widget.free_margin }),
            ...(widget.margin_level !== undefined && { marginLevel: widget.margin_level }),
          });
        }
      }

      // Update open/closed positions for the active account
      const positionList = data.account_info?.positions;
      if (positionList) {
        setPositions({
          open: positionList.open ?? [],
          closed: positionList.closed ?? [],
        });
      }

      // Update widget stats (KPI cards)
      if (data.widgets) {
        setWidgetStats({
          today_profit: data.widgets.dailyProfit?.today_profit ?? 0,
          percent_change: data.widgets.dailyProfit?.percent_change ?? 0,
          roi: data.widgets.roi?.today ?? 0,
          active_positions: data.widgets.active_positions ?? 0,
        });
      }

      // Update notifications (clientData sends the full list — replace it)
      if (Array.isArray(data.notifications)) {
        const mapped: Notification[] = data.notifications
          .filter((n) => !!n?.title)
          .map((n) => ({
            id: n.id ?? n.uuid ?? `ws-notif-${Date.now()}`,
            type: (n.type || 'info') as Notification['type'],
            title: n.title || '',
            message: n.message ?? '',
            read: n.read ?? false,
            createdAt: n.timestamp ?? n.createdAt ?? n.created_at ?? new Date().toISOString(),
          }));
        setNotifications(mapped);
      }
    },
    [activeAccount?.account_id, updateAccount, setNotifications]
  );

  // ── market.tick handler ──────────────────────────────────────────────────
  const handleMarketTick = useCallback((raw: unknown) => {
    const data = raw as MarketTickPayload;
    if (!data?.symbol) return;
    setTickers((prev) => ({
      ...prev,
      [data.symbol!]: {
        symbol: data.symbol!,
        bid: data.bid ?? 0,
        ask: data.ask ?? 0,
        change: data.change ?? 0,
        changePercent: data.changePercent ?? data.change_percent ?? 0,
        timestamp: data.timestamp ?? Date.now(),
      },
    }));
  }, []);

  // ── balance.update / account.update handler ────────────────────────────────
  const handleBalanceUpdate = useCallback(
    (raw: unknown) => {
      const data = raw as BalanceUpdatePayload;
      if (!data?.account_id) return;

      updateAccount(data.account_id, {
        ...(data.balance !== undefined && { balance: data.balance }),
        ...(data.equity !== undefined && { equity: data.equity }),
        ...(data.margin !== undefined && { margin: data.margin }),
        ...(data.free_margin !== undefined && { freeMargin: data.free_margin }),
        ...(data.margin_level !== undefined && { marginLevel: data.margin_level }),
      });

      if (activeAccount?.account_id === data.account_id) {
        setAccountWidget((prev) => ({
          balance: data.balance ?? prev?.balance ?? 0,
          equity: data.equity ?? prev?.equity ?? 0,
          free_margin: data.free_margin ?? prev?.free_margin ?? 0,
          margin: data.margin ?? prev?.margin ?? 0,
          pl: prev?.pl ?? 0,
          margin_level: data.margin_level ?? prev?.margin_level ?? 0,
          credit: prev?.credit ?? 0,
        }));
      }
    },
    [activeAccount?.account_id, updateAccount]
  );

  // ── positions.update handler ─────────────────────────────────────────────
  const handlePositionsUpdate = useCallback((raw: unknown) => {
    const data = raw as PositionsUpdatePayload;
    if (!data) return;
    setPositions({
      open: data.open ?? [],
      closed: data.closed ?? [],
    });
  }, []);

  // ── notifications topic handler (single object or array) ───────────────────
  const handleNotificationsTopic = useCallback(
    (raw: unknown) => {
      const incoming = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const mapped: Notification[] = [];

      incoming.forEach(
        (n: {
          title?: string;
          id?: string;
          uuid?: string;
          type?: string;
          message?: string;
          read?: boolean;
          timestamp?: string;
          createdAt?: string;
          created_at?: string;
        }) => {
          if (!n?.title) return;
          const id =
            n.id ?? n.uuid ?? `ws-notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          if (seenNotifIds.current.has(id)) return;
          seenNotifIds.current.add(id);
          mapped.push({
            id,
            type: (n.type || 'info') as Notification['type'],
            title: n.title,
            message: n.message ?? '',
            read: n.read ?? false,
            createdAt: n.timestamp ?? n.createdAt ?? n.created_at ?? new Date().toISOString(),
          });
        }
      );

      mapped.forEach((n) => addNotification(n));
    },
    [addNotification]
  );

  // ── errorResponse handler ─────────────────────────────────────────────────
  const handleErrorResponse = useCallback((raw: unknown) => {
    console.error('❌ Socket error:', raw);
  }, []);

  // ── subscribe on mount, unsubscribe on unmount ───────────────────────────
  useEffect(() => {
    const unsubClientData = wsManager.subscribe('clientData', handleClientData);
    const unsubBalance = wsManager.subscribe('balance.update', handleBalanceUpdate);
    const unsubAccount = wsManager.subscribe('account.update', handleBalanceUpdate);
    const unsubPositions = wsManager.subscribe('positions.update', handlePositionsUpdate);
    const unsubNotifications = wsManager.subscribe('notifications', handleNotificationsTopic);
    const unsubTick = wsManager.subscribe('market.tick', handleMarketTick);
    const unsubError = wsManager.subscribe('errorResponse', handleErrorResponse);

    return () => {
      unsubClientData();
      unsubBalance();
      unsubAccount();
      unsubPositions();
      unsubNotifications();
      unsubTick();
      unsubError();
    };
  }, [
    handleClientData,
    handleBalanceUpdate,
    handlePositionsUpdate,
    handleNotificationsTopic,
    handleMarketTick,
    handleErrorResponse,
  ]);

  return { tickers, widgetStats, accountWidget, positions };
}

export default useDashboardStreams;
